/**
 * AI Model Store - Centralized state for categories and AI models
 * Uses Zustand for state management with built-in caching to prevent duplicate API calls
 */
import { create } from "zustand";
import axios from "axios";
import { AIModel } from "../types/AIModel";

const baseUrl = process.env.REACT_APP_API_CONFIG;

export interface Category {
  id: number;
  documentId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  ai_models: AIModel[];
}

interface AIModelState {
  categories: Category[];
  models: AIModel[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null;

  fetchCategories: () => Promise<void>;
  getModelById: (id: string | number) => AIModel | undefined;
  reset: () => void;
}

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

export const useAIModelStore = create<AIModelState>((set, get) => ({
  categories: [],
  models: [],
  loading: false,
  error: null,
  lastFetched: null,

  fetchCategories: async () => {
    const { lastFetched, loading } = get();

    // Skip if already loading
    if (loading) {
      return;
    }

    // Skip if cache is still valid
    if (lastFetched && Date.now() - lastFetched < CACHE_DURATION) {
      return;
    }

    set({ loading: true, error: null });

    try {
      const response = await axios.get(
        `${baseUrl}/categories?populate=ai_models`
      );
      const categoriesData = response.data.data;

      // Flatten all models from all categories
      const allModels: AIModel[] = [];
      categoriesData.forEach((category: Category) => {
        if (category.ai_models && Array.isArray(category.ai_models)) {
          allModels.push(...category.ai_models);
        }
      });

      set({
        categories: categoriesData,
        models: allModels,
        loading: false,
        lastFetched: Date.now(),
      });
    } catch (err) {
      console.error("Error fetching categories:", err);
      set({
        error: "Failed to fetch AI models",
        loading: false,
      });
    }
  },

  getModelById: (id: string | number) => {
    const { models } = get();
    return models.find(
      (m) => m.id === id || m.documentId === id || m.id === Number(id)
    );
  },

  reset: () => {
    set({
      categories: [],
      models: [],
      loading: false,
      error: null,
      lastFetched: null,
    });
  },
}));
